import { PrismicRichText } from '@prismicio/react'
import styled from '@emotion/styled'
import { BREAKPOINT_MOBILE } from '../../util/breakpoints.ts'
import { InlineElementContainer } from '../atoms/Atoms.tsx'
import { ScrollstoryDocumentDataBodyQuoteSlicePrimary } from '../../../types.generated.ts'
import { asText } from '@prismicio/client'

interface InlineQuoteProps {
  quote: ScrollstoryDocumentDataBodyQuoteSlicePrimary
}

export const InlineQuote = (props: InlineQuoteProps) => {
  const name = asText(props.quote.person_name)
  const title = asText(props.quote.person_title)

  const hasImage = !!props.quote?.person_image?.url
  const imgAlt = props.quote?.person_image?.alt ?? `Image of ${name}`

  return (
    <QuoteContainer>
      <QuoteContent>
        <QuoteText>
          <PrismicRichText field={props.quote.text} />
        </QuoteText>
        <QuoteCaptionContainer>
          {hasImage && (
            <QuoteImage src={props.quote.person_image.url ?? ''} alt={imgAlt} />
          )}
          <QuoteCaption>
            <span>
              {name} <i>{title}</i>
            </span>
          </QuoteCaption>
        </QuoteCaptionContainer>
      </QuoteContent>
    </QuoteContainer>
  )
}

const QuoteContainer = styled(InlineElementContainer)`
  position: relative;
  max-width: 100%;
  background-color: #221f1f1a;
  ${({ theme }) => theme.quoteContainerCss};
`

// Calc for additional width: We make quotes slightly bigger than inline elements since we have some padding
// for lower responsive modes, but still want the text to take up the full width of inline element
const QuoteContent = styled.div`
  ${({ theme }) => {
    const marginLeftRight = theme.inlineElementAlign === 'center' ? 'auto' : '0'

    return `
        width: calc(${theme.inlineElementMaxWidth} + ${theme.inlinePaddingLeftRightDesktop} * 2);
        max-width: 100%;
        padding: 4em ${theme.inlinePaddingLeftRightDesktop};
        margin: ${marginLeftRight};

        @media ${BREAKPOINT_MOBILE} {
          padding: 3em ${theme.inlinePaddingLeftRightMobile};
        }

        display: flex;
        flex-direction: column;

        ${theme.quoteContentCss};
    `
  }}
`

const QuoteText = styled.blockquote`
  ${({ theme }) => theme.quoteTextCss};
`

const QuoteCaptionContainer = styled.div`
  ${({ theme }) => {
    return `
      width: calc(${theme.inlineTextMaxWidth} - ${theme.inlinePaddingLeftRightDesktop} * 2);
      max-width: 100%;
      margin: 1.5em auto 0 auto;

      @media ${BREAKPOINT_MOBILE} {
        padding: 0;
        margin: 1.5em 0 0 auto;
      }

      display:flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-start;

      ${theme.quoteCaptionContainerCss};
    `
  }}
`

const QuoteCaption = styled.div`
  display: flex;
  flex-direction: row;

  ${({ theme }) => theme.quoteCaptionCss};
`

const IMAGE_SIZE_DESKTOP = 40
const IMAGE_SIZE_MOBILE = 32

const QuoteImage = styled.img`
  width: ${IMAGE_SIZE_DESKTOP}px;
  height: ${IMAGE_SIZE_DESKTOP}px;
  margin-right: 15px;
  border-radius: 50%;

  @media ${BREAKPOINT_MOBILE} {
    margin-right: 11px;
    width: ${IMAGE_SIZE_MOBILE}px;
    height: ${IMAGE_SIZE_MOBILE}px;
  }
`
